import { Box, Text } from '@chakra-ui/react';
import React from 'react';

import type { SectionItem } from '../../config';
import { useCodingScreen } from '../../interface';
import { readState } from '../../slots/readState';
import { renderPlainText } from '../../slots/renderPlainText';
import { CARD_BG } from '../../styles/tokens';
import { Slot } from '../Slot';
import { useTabSelection } from '../TabSelectionContext';

/** Ascending by `order`, dropping any section marked `visible:false` -- the ordering/visibility
 *  contract every `Item`-driven container in this folder follows, kept local to
 *  this molecule folder per `PatientSummaryBar`/`CaseContextBar`/`DocumentTitleNav`'s precedent
 *  (each molecule folder stays self-contained). `.filter` on a `readonly SectionItem[]` already
 *  returns a fresh mutable array, so `.sort` never mutates the config's own (readonly, `as const`)
 *  section list. */
function visibleSectionsInOrder(
  sections: readonly SectionItem[]
): SectionItem[] {
  return sections
    .filter((section) => section.visible)
    .sort((a, b) => a.order - b.order);
}

/**
 * RIGHT-pane scrollable body -- will hold the active review tab's code sections in a future story
 * (`right.sections: SectionItem[]`); empty by default, no code data this story. `overflowY="auto"` +
 * `minH={0}` + `flex="1"` is the same "fills remaining space and scrolls independently" contract
 * every pane body follows -- mirrors `ClinicalDocumentViewer`'s contract exactly. By default
 * `right.sections` is `[]` so this renders an empty container; when a config supplies sections,
 * each renders as a labelled `Box` (a reused Chakra `Text` heading + `data-section-key`), proving
 * the atom-reuse requirement.
 *
 * Each section's `Box` is NON-INTERACTIVE chrome the host always renders (`key`/`data-slot-key`/
 * `data-section-key` stay on the `Box`); the section's BODY is a `Slot`
 * reading `section.contentId`'s bound value out of injected `state` (`slots/readState.ts`) -- when
 * `section.componentId` resolves in the injected registry the component renders in place of the
 * heading, otherwise the fallback (the pre-existing `<Text as="h3">` heading, now showing
 * `renderPlainText(value) ?? section.label`) reproduces exactly what this molecule always rendered:
 * the bound value as text, or `section.label` when there is no bound value (the empty-shell
 * default, `value === undefined` -> `renderPlainText` returns `null` -> the `??` falls through to
 * the label, so the shipped DOM is unchanged).
 *
 * TAB SELECTION: a selected
 * RIGHT-pane review tab's slot renders FIRST, ABOVE the section list -- not in
 * `ReviewTransactionsTabs`' own (deliberately empty) `TabPanel`. `visibleTabs` is the SAME
 * visible-and-order-sorted list `ReviewTransactionsTabs`' own strip renders from; `selectedTab`
 * looks up the CURRENTLY SELECTED tab (`useTabSelection()`, scoped per pane by the ancestor
 * `TabSelectionProvider`) within that list, falling back to `visibleTabs[0]` -- the SAME tab the
 * controlled strip's `Math.max(0, visibleTabs.findIndex(...))` shows highlighted whenever
 * `selectedContentKey` is `undefined` or stale/hidden (the body must agree with what the
 * strip actually displays, not silently render nothing while the strip highlights a tab). So
 * whenever ANY visible tab exists, `selectedTab` is always defined and its `Slot` always renders;
 * the tab-slot `Box` below is omitted ONLY when `right.tabs` has no visible entries at all (the true
 * empty-shell case, byte-identical to before this task). The selected tab's `fallback` falls
 * all the way back to its own `label` (`() => renderPlainText(selectedValue) ?? selectedTab.label`)
 * so a selected tab with no bound state value shows its label instead of rendering blank -- unlike
 * `ClinicalDocumentViewer`'s datum-marker case, `ReviewContent`'s own `reviewContent` marker is a
 * pure `ContainerMarker` with no bound datum and is never itself rendered through a `Slot`, so there
 * is no separate "no label" fallback branch here. The selected tab's `Slot` is wrapped in its own
 * `<Box data-slot-key={selectedTab.contentKey}>` so it carries a stable identity the same way every
 * other leaf item in this folder does (a container's own `data-content-key` stays on the region
 * `<Box>` below, unchanged).
 */
export const ReviewContent: React.FC = () => {
  const { config, state } = useCodingScreen();
  const { mode } = config;
  const { reviewContent, sections } = config.right;
  const visibleSections = visibleSectionsInOrder(sections);
  const { selectedContentKey } = useTabSelection();
  const visibleTabs = config.right.tabs
    .filter((tab) => tab.visible)
    .sort((a, b) => a.order - b.order);
  const selectedTab =
    visibleTabs.find((tab) => tab.contentKey === selectedContentKey) ??
    visibleTabs[0];
  const selectedValue = selectedTab
    ? readState(state, selectedTab.contentId)
    : undefined;

  return (
    <Box
      role="region"
      aria-label="Review content"
      data-content-key={reviewContent.contentKey}
      overflowY="auto"
      minH={0}
      flex="1"
      bg={CARD_BG}
    >
      {selectedTab && (
        <Box data-slot-key={selectedTab.contentKey}>
          <Slot
            item={selectedTab}
            value={selectedValue}
            mode={mode}
            fallback={() => renderPlainText(selectedValue) ?? selectedTab.label}
          />
        </Box>
      )}
      {visibleSections.map((section) => {
        const value = readState(state, section.contentId);
        return (
          <Box
            key={section.contentKey}
            data-slot-key={section.contentKey}
            data-section-key={section.contentKey}
            px={3}
            py={2}
          >
            <Slot
              item={section}
              value={value}
              mode={mode}
              fallback={() => (
                <Text as="h3" textStyle="h3">
                  {renderPlainText(value) ?? section.label}
                </Text>
              )}
            />
          </Box>
        );
      })}
    </Box>
  );
};
